import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Grid, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import toast from 'react-hot-toast';
import dayjs, { type Dayjs } from 'dayjs';
import { DiagnosisCreateDTO, DiagnosisReadDTO, UserReadDTO } from '../../../generated/api-client';
import { useUpdateDiagnosis } from '../../../hooks';
import { DiagnosisStepHandle } from './Step.types';
import { DropdownEntity } from '../../common';
import { apiClient } from '../../../services/apiClient';

interface DiagnosisDetailsStepProps {
  diagnosisId?: string;
  data?: DiagnosisReadDTO | null;
}

type DiagnosisDetailsFormValues = {
  table_code_index: string;
  site_id?: string;
  reference_period_from: Dayjs | null;  // Data inizio periodo riferimento (12 mesi)
  reference_period_to: Dayjs | null;    // Data fine periodo riferimento (12 mesi)
  data_generated_from: Dayjs | null;   // Dati generali da
  data_generated_to: Dayjs | null;     // Dati generali a
  responsible_01: string | null;
  responsible_02?: string | null;
  responsible_03?: string | null;
  diagnosis_date: Dayjs | null;
};

const defaultValues: DiagnosisDetailsFormValues = {
  table_code_index: '',
  site_id: undefined,
  reference_period_from: null,
  reference_period_to: null,
  data_generated_from: null,
  data_generated_to: null,
  responsible_01: null,
  responsible_02: null,
  responsible_03: null,
  diagnosis_date: null,
};

/** Giorno di calendario locale → stringa `YYYY-MM-DD` per i DTO API. */
function dayjsToApiDateString(d: Dayjs | null | undefined): string | undefined {
  if (!d?.isValid()) return undefined;
  return d.format('YYYY-MM-DD');
}

export const DiagnosisDetailsStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisDetailsStepProps>(({ diagnosisId, data }, ref) => {
    const { readOnly } = useDiagnosisPermission();
    const {
      control,
      register,
      reset,
      trigger,
      getValues,
      watch,
      setValue,
      formState: { errors, isDirty },
    } = useForm<DiagnosisDetailsFormValues>({
      defaultValues,
    });

    // Watch tutti i campi per forzare il re-render quando cambiano
    watch();

    const updateDiagnosis = useUpdateDiagnosis();

    useEffect(() => {
      if (data) {
        reset({
          table_code_index: data.table_code_index || '',
          site_id: data.site_id || undefined,
          reference_period_from: data.reference_period_from
            ? dayjs(data.reference_period_from)
            : null,
          reference_period_to: data.reference_period_to ? dayjs(data.reference_period_to) : null,
          data_generated_from: data.data_generated_from
            ? dayjs(data.data_generated_from)
            : null,
          data_generated_to: data.data_generated_to ? dayjs(data.data_generated_to) : null,
          responsible_01: data.responsible_01 || null,
          responsible_02: data.responsible_02 || null,
          responsible_03: data.responsible_03 || null,
          diagnosis_date: data.diagnosis_date ? dayjs(data.diagnosis_date) : null,
        });
      } else {
        reset(defaultValues);
      }
    }, [data, reset]);

    const diagnosisDate = watch('diagnosis_date');
    const referencePeriodFrom = watch('reference_period_from');
    const referencePeriodTo = watch('reference_period_to');

    // Se selezioni la data diagnosi e le date di riferimento sono vuote → 1/1/(anno diagnosi - 1) e 31/12/(anno diagnosi - 1)
    useEffect(() => {
      if (
        diagnosisDate?.isValid() &&
        diagnosisDate.year() >= 1000 &&
        referencePeriodFrom == null &&
        referencePeriodTo == null
      ) {
        const year = diagnosisDate.year() - 1;
        setValue('reference_period_from', dayjs(`${year}-01-01`), { shouldDirty: false });
        setValue('reference_period_to', dayjs(`${year}-12-31`), { shouldDirty: false });
      }
    }, [diagnosisDate, referencePeriodFrom, referencePeriodTo, setValue]);

    // Periodo dati generati: da 1/1/(annoDiagnosi - 3) a 31/12/annoDiagnosi quando cambia la data diagnosi
    useEffect(() => {
      if (diagnosisDate?.isValid() && diagnosisDate.year() >= 1000) {
        const diagnosisYear = diagnosisDate.year();
        const newDataGeneratedFrom = dayjs(`${diagnosisYear - 3}-01-01`);
        const newDataGeneratedTo = dayjs(`${diagnosisYear}-12-31`);
        setValue('data_generated_from', newDataGeneratedFrom, { shouldDirty: false });
        setValue('data_generated_to', newDataGeneratedTo, { shouldDirty: false });
      }
    }, [diagnosisDate, setValue]);

    const performSave = async (payload: DiagnosisDetailsFormValues) => {
      if (!diagnosisId) {
        return { success: true };
      }
      // Il Responsabile 1 è obbligatorio anche a livello di database (colonna NOT NULL):
      // invece di inviare '' (errore FK lato server) si blocca il salvataggio con un messaggio chiaro.
      if (!payload.responsible_01) {
        toast.error('Il Responsabile 1 è obbligatorio: assegnane uno prima di salvare');
        return { success: false };
      }
      try {
        await updateDiagnosis.mutateAsync({
          id: diagnosisId,
          data: new DiagnosisCreateDTO({
            table_code_index: payload.table_code_index,
            site_id: payload.site_id,
            reference_period_from: dayjsToApiDateString(payload.reference_period_from),
            reference_period_to: dayjsToApiDateString(payload.reference_period_to),
            data_generated_from: dayjsToApiDateString(payload.data_generated_from),
            data_generated_to: dayjsToApiDateString(payload.data_generated_to),
            responsible_01: payload.responsible_01,
            // null esplicito: con `undefined` JSON.stringify scarta la chiave e il backend
            // (update con exclude_unset) manterrebbe il responsabile appena rimosso.
            responsible_02: (payload.responsible_02 || null) as unknown as string | undefined,
            responsible_03: (payload.responsible_03 || null) as unknown as string | undefined,
            diagnosis_date: dayjsToApiDateString(payload.diagnosis_date),
          }),
        });
        toast.success('Dati diagnosi salvati');
        return { success: true };
      } catch (error: any) {
        const message = error?.message || 'Errore nel salvataggio della diagnosi';
        toast.error(message);
        return { success: false };
      }
    };

    React.useImperativeHandle(ref, () => ({
      save: async (force = false) => {
        const isValid = force ? true : await trigger(undefined, { shouldFocus: true });
        if (!isValid && !force) {
          return { success: false, requiresConfirmation: true };
        }
        const values = getValues();
        return performSave(values);
      },
    }));

    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Typography variant="h6">Diagnosi</Typography>
        <Grid container spacing={2}>
          {/* Riga 1: solo codice diagnosi (più largo del doppio) */}
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              label="Codice diagnosi"
              fullWidth
              required
              error={!!errors.table_code_index}
              helperText={errors.table_code_index?.message}
              {...register('table_code_index', { required: 'Campo obbligatorio' })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            </Grid>


          {/* Riga 3: data diagnosi */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="diagnosis_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Data diagnosi"
                  value={field.value}
                  onChange={value => field.onChange(value ?? null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            </Grid>

          {/* Riga 4: periodo di riferimento (da → to = +1 anno -1 gg; to → da = -1 anno +1 gg) */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="reference_period_from"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Data inizio periodo riferimento"
                  value={field.value}
                  onChange={value => {
                    field.onChange(value ?? null);
                    if (value?.isValid()) {
                      const to = value.add(1, 'year').subtract(1, 'day');
                      setValue('reference_period_to', to, { shouldDirty: true });
                    }
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="reference_period_to"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Data fine periodo riferimento"
                  value={field.value}
                  onChange={value => {
                    field.onChange(value ?? null);
                    if (value?.isValid()) {
                      const from = value.subtract(1, 'year').add(1, 'day');
                      setValue('reference_period_from', from, { shouldDirty: true });
                    }
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            </Grid>
          {/* Riga 5: date dati generati */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="data_generated_from"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Dati generali da"
                  value={field.value}
                  onChange={value => field.onChange(value ?? null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="data_generated_to"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Dati generali a"
                  value={field.value}
                  onChange={value => field.onChange(value ?? null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
          </Grid>

          {/* Riga 2: responsabili */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="responsible_01"
              control={control}
              rules={{ required: 'Campo obbligatorio' }}
              render={({ field, fieldState }) => (
                <DropdownEntity<UserReadDTO>
                  label="Responsabile 1"
                  value={field.value ?? null}
                  queryKey={['users', 'dropdown']}
                  fetchEntities={() => apiClient.list_items_user__get()}
                  getOptionLabel={option =>
                    `${option.firstname || ''} ${option.lastname || ''} (${option.email || ''})`.trim()
                  }
                  getOptionValue={option => option.id ?? ''}
                  onChange={value => field.onChange(value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  placeholder="Seleziona il responsabile 1"
                  required
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="responsible_02"
              control={control}
              render={({ field, fieldState }) => (
                <DropdownEntity<UserReadDTO>
                  label="Responsabile 2"
                  value={field.value ?? null}
                  queryKey={['users', 'dropdown']}
                  fetchEntities={() => apiClient.list_items_user__get()}
                  getOptionLabel={option =>
                    `${option.firstname || ''} ${option.lastname || ''} (${option.email || ''})`.trim()
                  }
                  getOptionValue={option => option.id ?? ''}
                  onChange={value => field.onChange(value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  placeholder="Seleziona il responsabile 2"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="responsible_03"
              control={control}
              render={({ field, fieldState }) => (
                <DropdownEntity<UserReadDTO>
                  label="Responsabile 3"
                  value={field.value ?? null}
                  queryKey={['users', 'dropdown']}
                  fetchEntities={() => apiClient.list_items_user__get()}
                  getOptionLabel={option =>
                    `${option.firstname || ''} ${option.lastname || ''} (${option.email || ''})`.trim()
                  }
                  getOptionValue={option => option.id ?? ''}
                  onChange={value => field.onChange(value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  placeholder="Seleziona il responsabile 3"
                />
              )}
            />
          </Grid>
        </Grid>
      </Stack>
    );
  })
);

DiagnosisDetailsStep.displayName = 'DiagnosisDetailsStep';
